import React, { useContext, useEffect } from 'react'
import { Typography } from '@mui/material'
import {
  MAX_IDENTITY_VERIFICATION_ATTEMPTS,
  VerificationStatus,
} from '@playback-rewards/shared-libraries'

import { useNavigate } from 'react-router-dom'
import MessageLayout from '../components/layout/Message'
import { handleStripeIdentity, handleVerisoulIdentity, sendRNMessage } from '../utils/utils'
import { REACT_NATIVE_STATUS, TERMS_OF_SERVICE_LINK } from '../constants'
import { IDENTITY_PROVIDER, resolveIdentityProvider } from '../utils/identityProvider'
import AuthContext from '../context/AuthContext'
import NativeAppContext from '../context/NativeApp/NativeAppContext'

const getExistingVerificationRoute = ({ attempts, status }) => {
  if (status === VerificationStatus.VERIFIED) {
    return '/rewards'
  }

  if (
    status === VerificationStatus.VERIFYING ||
    attempts >= MAX_IDENTITY_VERIFICATION_ATTEMPTS ||
    status === VerificationStatus.PLAYBACK_DUPLICATE_REJECTED
  ) {
    return '/verification/result'
  }

  return null
}

const NeedVerification = () => {
  const { user } = useContext(AuthContext)
  const { nativeAppDetails } = useContext(NativeAppContext)
  const navigate = useNavigate()

  useEffect(() => {
    const existingVerificationRoute = getExistingVerificationRoute(user.identityVerification)

    if (existingVerificationRoute) {
      navigate(existingVerificationRoute)
    }
  }, [user.identityVerification.attempts, user.identityVerification.status, navigate])

  const handleClick = async () => {
    const existingVerificationRoute = getExistingVerificationRoute(user.identityVerification)

    if (existingVerificationRoute) {
      navigate(existingVerificationRoute)
      return
    }

    const provider = resolveIdentityProvider({
      isNativeApp: Boolean(window.ReactNativeWebView),
      versionCode: nativeAppDetails?.versionCode,
    })
    if (window.ReactNativeWebView) {
      return sendRNMessage({
        status:
          provider === IDENTITY_PROVIDER.VERISOUL
            ? REACT_NATIVE_STATUS.START_IDENTITY_VERIFICATION
            : REACT_NATIVE_STATUS.CAMERA_PERMISSION,
      })
    }

    if (provider === IDENTITY_PROVIDER.STRIPE) {
      handleStripeIdentity(user.id, navigate)
      return
    }

    handleVerisoulIdentity(user.id)
  }

  const handleBackArrowClick = () => {
    navigate('/rewards')
  }

  return (
    <MessageLayout
      title="Verify Your Identity"
      button={{
        text: 'Continue',
        handleClick,
      }}
      handleBackArrowClick={handleBackArrowClick}
    >
      <Typography variant="body2">
        You are required to verify your account with a valid ID in order to redeem rewards. This is
        necessary to protect the Playback system from abuse and to ensure users are compliant with
        our <a href={TERMS_OF_SERVICE_LINK}>Terms of Service</a>.
      </Typography>
      <Typography variant="body2">
        <b>
          We only allow one account per person, and the ID used to verify the account must be that
          of the account holder.
        </b>{' '}
        Playback is only available to those 18 years and older who are based in eligible
        territories.
      </Typography>
      <Typography variant="body2">
        The next couple of screens will require you to take a picture of your ID card and a selfie.
        This information is only used to verify your account and for fraud prevention purposes.
      </Typography>
    </MessageLayout>
  )
}

export default NeedVerification
